/**
 * App Store-style 3-up gallery with header pill, stars rating, captions.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { phoneFrame, screenContent } from "./_mockHelpers.js";

export const appStoreShowcase: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const pill = inputs.text ?? "Top-Rated";
  const headline = inputs.subtext ?? "";
  const captions = (inputs.items?.length ? inputs.items : ["Suchen", "Buchen", "Genießen"]).slice(0, 3);
  const isVertical = ctx.format === "9:16";
  const phoneW = isVertical ? 260 : 290;

  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const pillId = `${clipPrefix}-pill`;
  const headId = `${clipPrefix}-head`;
  const phoneIds = captions.map((_, i) => `${clipPrefix}-p-${i}`);
  const capIds = captions.map((_, i) => `${clipPrefix}-c-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  const phones = captions.map((cap, i) => {
    const inner = screenContent({ media: inputs.media, mediaIndex: i, accent, fallback: i === 0 ? "list" : i === 1 ? "feed" : "card", fallbackTitle: cap });
    return `<div style="display:flex;flex-direction:column;align-items:center;gap:14px;">
      ${phoneFrame({ id: phoneIds[i]!, width: phoneW, accent, inner, tilt: i === 0 ? -3 : i === 2 ? 3 : 0 })}
      <div id="${capIds[i]}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.78}px;letter-spacing:1.5px;color:${themeColors.fg};text-align:center;">${i + 1}. ${escapeHtml(cap)}</div>
    </div>`;
  }).join("");

  const stars = Array(5).fill(0).map(() => `<span style="color:${accent};font-size:${sm * 0.85}px;text-shadow:0 0 12px ${withAlpha(accent, 0.6)};">★</span>`).join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:36px;padding:64px;">
    <div id="${pillId}" style="display:inline-flex;align-items:center;gap:14px;padding:12px 22px;border-radius:9999px;background:${withAlpha(accent, 0.16)};border:1.5px solid ${withAlpha(accent, 0.45)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.7}px;letter-spacing:4px;text-transform:uppercase;color:${accent};">
      <span>${stars}</span>
      <span>${escapeHtml(pill)}</span>
    </div>
    ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.78}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1.02;text-align:center;text-wrap:balance;max-width:${isVertical ? 820 : 1100}px;">${escapeHtml(headline)}</div>` : ""}
    <div style="display:flex;align-items:flex-end;justify-content:center;gap:${isVertical ? 18 : 32}px;flex-wrap:${isVertical ? "wrap" : "nowrap"};">${phones}</div>
  </div>`;

  const init = [
    gsapSetLine(`#${pillId}`, { opacity: 0, y: -14, scale: 0.9 }),
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 14 }) : "",
    ...phoneIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 60, scale: 0.92 })),
    ...capIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 8 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${pillId}`, { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "raw:\"back.out(1.6)\"" }, 0.05),
  ];
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.2));
  phoneIds.forEach((id, i) => tweens.push(t.to(`#${id}`, { opacity: 1, y: 0, scale: 1, duration: 0.75, ease: "raw:\"power3.out\"" }, 0.4 + i * 0.12)));
  capIds.forEach((id, i) => tweens.push(t.to(`#${id}`, { opacity: 1, y: 0, duration: 0.45, ease: "raw:\"power2.out\"" }, 0.65 + i * 0.12)));
  phoneIds.forEach((id, i) => tweens.push(t.to(`#${id}`, { y: i === 1 ? -8 : -4, duration: Math.max(1.0, clipDurationSec - 2.0), ease: "raw:\"sine.inOut\"" }, 1.4)));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
